/* eslint-disable import/prefer-default-export */
import { defineStore } from "pinia";
import { isBoolean } from "@vueuse/core";
import useApplePay from "@/components/forms/Payments/Worldpayapple/useApplePay";
import useCountryEndpoints from "@/composables/api/useCountryEndpoints";
import useLocalStorage from "@/composables/storage/useLocalStorage.js";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import usePaymentEndpoints from "@/composables/api/usePaymentEndpoints";
const { checkLocalStorage, bindLocalStorage } = useLocalStorage();
const { getCountries } = useCountryEndpoints();
const { getPayments } = usePaymentEndpoints();
const { isApplePayAvailable } = useApplePay();

export const ORDER_TYPE = {
  BUY: "BUY",
  SELL: "SELL",
};

export const globalOptions = {
  payments: [],
  payouts: [],
  nftPayments: [],
  countries: [],
  customerId: "",
  customerReference: "",
  showSettings: false,
  theme: null,
  merchant: null,
  language: "EN", // TODO: to be generated by API probably
  isFormLoading: false, // TODO: to be generated by API probably,
  isShowSplash: false,
  detectedGeo: null,
};

/**
 * checks user's local browser + if it has applepaySession
 * @param {Array} payments
 * @returns {Array}
 */
const removeApplepayByUserSession = (payments) => {
  // payments.splice(payments.indexOf(paymentToRemove), 1);
  if (!isApplePayAvailable()) {
    return payments.filter((payment) => {
      return payment.paymentType !== "WORLDPAYAPPLE";
    });
  }

  return payments;
};

export const useGlobalStore = defineStore({
  id: "global",
  state: () => ({
    global: checkLocalStorage("global", globalOptions),
    fetchingToken: false,
    token: null,
    sessionToken: null,
  }),
  actions: {
    getSessionToken() {
      const storedToken = checkLocalStorage("token", false);

      if (storedToken) {
        this.sessionToken = storedToken;
      }
    },
    setCountries(countries) {
      this.global.countries = countries;
    },
    setLanguage(language) {
      this.global.language = language;
    },
    setIsFormLoading(isLoading) {
      this.global.isFormLoading = isLoading;
    },
    setMerchantSettings(settings) {
      this.global.merchant = settings;
    },
    toggleSettings(value) {
      if (isBoolean(value)) {
        this.global.showSettings = value;
      }
      if (value === undefined) {
        this.global.showSettings = Boolean(!this.global.showSettings);
      }
      bindLocalStorage(this.$id, this.global);
    },
    toggleTheme() {
      const newTheme =
        this.global.theme === undefined
          ? "DARK"
          : this.global.theme === "LIGHT"
          ? "DARK"
          : "LIGHT";
      this.global.theme = newTheme;
      bindLocalStorage(this.$id, this.global);
    },

    setTheme(theme) {
      this.global.theme = theme;
      bindLocalStorage(this.$id, this.global);
    },
    async fetchCountries() {
      const res = await getCountries();
      const countries = [];
      // delete when api is fixed
      for (const country of res.data.data.countries) {
        countries.push({ name: country[0], code: country[1].toUpperCase() });
      }
      const sortedCountries = countries.sort((itemA, itemB) =>
        itemA.name < itemB.name ? -1 : itemA.name > itemB.name ? 1 : 0
      );
      this.global.countries = sortedCountries;
    },
    async fetchPayments() {
      const orderStore = useOrderFormStore();

      const res = await getPayments();

      const { payment_methods, payouts, nft_payments } = res.data.data;
      if (payment_methods) {
        const filteredPayments = removeApplepayByUserSession(payment_methods);
        this.global.payments = filteredPayments;

        if (!payouts) {
          orderStore.setActiveMode("BUY");
        }
      }

      if (payouts) {
        if (!payment_methods) {
          orderStore.setActiveMode("SELL");
        }
        this.global.payouts = payouts;
      }

      if (nft_payments) {
        const filterdNFTPayments = removeApplepayByUserSession(nft_payments);
        this.global.nftPayments = filterdNFTPayments;
      }
      bindLocalStorage(this.$id, this.global);
    },
    setCustomerId(customerId) {
      this.global.customerId = customerId;
      bindLocalStorage(this.$id, this.global);
    },
    setCustomerReference(customerReference) {
      this.global.customerReference = customerReference;
      bindLocalStorage(this.$id, this.global);
    },
    setShowSplashScreen(isShow) {
      this.global.isShowSplash = isShow;
      bindLocalStorage(this.$id, this.global);
    },
    setDetectedGeo(geo) {
      this.global.detectedGeo = geo;
      bindLocalStorage(this.$id, this.global);
    },
  },
  getters: {
    getPayments: (store) => {
      return store.global.payments;
    },
    getPayouts: (store) => {
      return store.global.payouts;
    },
    getNFTPayments: (store) => {
      return store.global.nftPayments;
    },
    getCountries: (store) => {
      return store.global.countries;
    },
    getCountryCode: (store) => (countryName) => {
      const findMatchingCountry = store.global.countries.find(
        (country) => country.name === countryName
      );
      if (!findMatchingCountry) {
        return countryName;
      }

      return findMatchingCountry.code;
    },
    getIsLightMode: (store) => {
      return store.global.theme === "LIGHT";
    },
    getIsDarkMode: (store) => {
      return store.global.theme === "DARK";
    },
    getIsThemeSetted: (store) => {
      return store.global.theme !== null;
    },
    getCustomerId: (store) => {
      return store.global.customerId;
    },
    getCustomerReference: (store) => {
      return store.global.customerReference;
    },
    getMerchantSettings: (store) => {
      return store.global.merchant;
    },
    getIsFormLoading: (store) => {
      return store.global.isFormLoading;
    },
    getIsInIFrame: () => {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
    getIsGreyLabel: (store) => {
      return !!(store.global.merchant?.theme?.hide_logo === true);
    },
    getDetectedGeo: (store) => {
      return store.global.detectedGeo;
    },
  },
});
